import type { ClassValue } from 'clsx';

import { clsxm } from '@vs-shop/utils';

import {
  ProductCardActionIcons,
  ProductCardActions,
  type ProductCardVariant,
} from '@vs-shop/commerce-ui/productCard';

import { getMemoizedIntlShapeWithDefaultLocale } from '../../../../utils/intl';

import ProductCardComparisonButton from '../../../client/product/card/ProductCardComparisonButton';
import ProductCardCtaLink from '../../../client/product/card/ProductCardCtaLink';
import ProductFavouritesModal from '../../../client/product/favourites/ProductFavouritesModal';

type Props = {
  productCode: string;
  productName: string;
  isFavourite: boolean;
  routerUrl: string;
  metaTitle: string;
  variant: ProductCardVariant;
  className?: ClassValue;
};

/**
 * rcprofi's controls at the foot of the card.
 *
 * The primary control opens the product detail; comparison and favourites sit
 * beside it. What the control does, and what it says, is this tenant's — the
 * section only decides that it fills the row it shares. The detail link is
 * handed over as data, not as an element: this component renders on the server
 * for the related-products rail, and an element passed into a client prop can
 * arrive there as a lazy reference (see `ProductCardCtaLink`).
 */
export default function ProductCardButtons({
  productCode,
  productName,
  isFavourite,
  routerUrl,
  metaTitle,
  variant,
  className,
}: Props) {
  const intl = getMemoizedIntlShapeWithDefaultLocale();

  return (
    <ProductCardActions variant={variant} className={clsxm(className)}>
      <ProductCardCtaLink href={routerUrl} title={metaTitle}>
        {intl.formatMessage({
          id: 'productCard.button.detail',
          defaultMessage: 'DETAIL',
        })}
      </ProductCardCtaLink>

      <ProductCardActionIcons>
        <ProductCardComparisonButton productCode={productCode} />
        <ProductFavouritesModal
          variant="card"
          productCode={productCode}
          productName={productName}
          isFavourite={isFavourite}
        />
      </ProductCardActionIcons>
    </ProductCardActions>
  );
}
